/**
 * Supabase Exemplar Repository
 * Handles exemplar CRUD and test retrieval for the AI Sandbox
 */

import { requireSupabaseClient } from '@/lib/supabase'
import type {
  IExemplarRepository,
  Exemplar,
  CreateExemplarData,
  UpdateExemplarData,
  ExemplarMatch,
  ExemplarCategory,
  BulkImportResult,
} from '../interfaces/IExemplarRepository'

const DEFAULT_PAGE_SIZE = 20

export class SupabaseExemplarRepository implements IExemplarRepository {
  async listExemplars(params?: {
    page?: number
    pageSize?: number
  }): Promise<{ data: Exemplar[]; total: number }> {
    const supabase = requireSupabaseClient()
    const page = params?.page ?? 1
    const pageSize = params?.pageSize ?? DEFAULT_PAGE_SIZE
    const from = (page - 1) * pageSize
    const to = from + pageSize - 1

    const { data, error, count } = await supabase
      .from('exemplars')
      .select('*', { count: 'exact' })
      .order('created_at', { ascending: false })
      .range(from, to)

    if (error) {
      throw new Error(`Failed to fetch exemplars: ${error.message}`)
    }

    return {
      data: (data || []).map(this.mapToExemplar),
      total: count || 0,
    }
  }

  async createExemplar(data: CreateExemplarData): Promise<Exemplar> {
    const supabase = requireSupabaseClient()

    const {
      data: { user },
      error: authError,
    } = await supabase.auth.getUser()
    if (authError || !user) throw new Error('Not authenticated')

    const { data: exemplar, error: insertError } = await supabase
      .from('exemplars')
      .insert({
        question: data.question,
        answer: data.answer,
        category: data.category,
        tags: data.tags || [],
        source_type: data.sourceType,
        source_conversation_id: data.sourceConversationId || null,
        source_message_id: data.sourceMessageId || null,
        status: 'pending',
        is_active: true,
        created_by: user.id,
        last_edited_by: user.id,
      } as Record<string, unknown>)
      .select()
      .single()

    if (insertError || !exemplar) {
      throw new Error(
        `Failed to create exemplar: ${insertError?.message || 'No data returned'}`
      )
    }

    // Trigger embedding generation (fire and forget)
    supabase.functions
      .invoke('process-exemplar', {
        body: { exemplarId: exemplar.id },
      })
      .catch((err: Error) => {
        console.error('Failed to trigger exemplar processing:', err)
      })

    return this.mapToExemplar(exemplar)
  }

  async updateExemplar(
    id: string,
    data: UpdateExemplarData
  ): Promise<Exemplar> {
    const supabase = requireSupabaseClient()

    const {
      data: { user },
      error: authError,
    } = await supabase.auth.getUser()
    if (authError || !user) throw new Error('Not authenticated')

    // Build update payload — only include fields that are provided
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const updates: Record<string, any> = {
      last_edited_by: user.id,
      updated_at: new Date().toISOString(),
      status: 'pending', // Re-embed on update
      error_message: null,
    }
    if (data.question !== undefined) updates.question = data.question
    if (data.answer !== undefined) updates.answer = data.answer
    if (data.category !== undefined) updates.category = data.category
    if (data.tags !== undefined) updates.tags = data.tags

    const { data: exemplar, error: updateError } = await supabase
      .from('exemplars')
      .update(updates)
      .eq('id', id)
      .select()
      .single()

    if (updateError || !exemplar) {
      throw new Error(
        `Failed to update exemplar: ${updateError?.message || 'No data returned'}`
      )
    }

    // Re-trigger embedding generation (fire and forget)
    supabase.functions
      .invoke('process-exemplar', {
        body: { exemplarId: id },
      })
      .catch((err: Error) => {
        console.error('Failed to trigger exemplar re-processing:', err)
      })

    return this.mapToExemplar(exemplar)
  }

  async deleteExemplar(id: string): Promise<void> {
    const supabase = requireSupabaseClient()

    const { error } = await supabase.from('exemplars').delete().eq('id', id)

    if (error) {
      throw new Error(`Failed to delete exemplar: ${error.message}`)
    }
  }

  async toggleActive(id: string, isActive: boolean): Promise<void> {
    const supabase = requireSupabaseClient()

    const { error } = await supabase
      .from('exemplars')
      .update({
        is_active: isActive,
        updated_at: new Date().toISOString(),
      })
      .eq('id', id)

    if (error) {
      throw new Error(`Failed to toggle exemplar: ${error.message}`)
    }
  }

  async testRetrieval(query: string): Promise<ExemplarMatch[]> {
    const supabase = requireSupabaseClient()

    const { data, error } = await supabase.functions.invoke(
      'process-exemplar',
      {
        body: { action: 'test-retrieval', query },
      }
    )

    if (error) {
      throw new Error(`Test retrieval failed: ${error.message}`)
    }

    return (data?.matches || []).map(
      (m: {
        id: string
        question: string
        answer: string
        category: string
        similarity: number
      }) => ({
        id: m.id,
        question: m.question,
        answer: m.answer,
        category: m.category,
        similarity: m.similarity,
      })
    )
  }

  async bulkImportExemplars(
    exemplars: Array<{
      question: string
      answer: string
      category: ExemplarCategory
      tags?: string[]
    }>
  ): Promise<BulkImportResult> {
    const supabase = requireSupabaseClient()

    const { data, error } = await supabase.functions.invoke(
      'process-exemplar',
      {
        body: { action: 'bulk-import', exemplars },
      }
    )

    if (error) {
      throw new Error(`Bulk import failed: ${error.message}`)
    }

    return {
      imported: data?.imported ?? 0,
      skippedDuplicates: data?.skippedDuplicates ?? 0,
      failed: data?.failed ?? 0,
      batchId: data?.batchId ?? '',
      errors: data?.errors ?? [],
    }
  }

  async bulkImportFromPdf(
    file: File,
    defaultCategory: ExemplarCategory
  ): Promise<BulkImportResult> {
    const supabase = requireSupabaseClient()

    const {
      data: { user },
      error: authError,
    } = await supabase.auth.getUser()
    if (authError || !user) throw new Error('Not authenticated')

    // Upload PDF to temporary storage path
    const storagePath = `${user.id}/temp/${Date.now()}_${file.name}`
    const { error: uploadError } = await supabase.storage
      .from('rag-documents')
      .upload(storagePath, file)

    if (uploadError) {
      throw new Error(`Failed to upload PDF: ${uploadError.message}`)
    }

    // Call edge function with storage path
    const { data, error } = await supabase.functions.invoke(
      'process-exemplar',
      {
        body: {
          action: 'bulk-import-pdf',
          storagePath,
          defaultCategory,
        },
      }
    )

    if (error) {
      // Clean up uploaded file on error
      await supabase.storage.from('rag-documents').remove([storagePath])
      throw new Error(`Bulk PDF import failed: ${error.message}`)
    }

    return {
      imported: data?.imported ?? 0,
      skippedDuplicates: data?.skippedDuplicates ?? 0,
      failed: data?.failed ?? 0,
      batchId: data?.batchId ?? '',
      errors: data?.errors ?? [],
      parsedCount: data?.parsedCount,
    }
  }

  async deleteBatch(batchId: string): Promise<number> {
    const supabase = requireSupabaseClient()

    // Find all exemplars with the batch tag
    const { data: matches, error: findError } = await supabase
      .from('exemplars')
      .select('id')
      .contains('tags', [`batch:${batchId}`])

    if (findError) {
      throw new Error(`Failed to find batch exemplars: ${findError.message}`)
    }

    if (!matches || matches.length === 0) {
      return 0
    }

    const ids = matches.map((m: { id: string }) => m.id)
    const { error: deleteError } = await supabase
      .from('exemplars')
      .delete()
      .in('id', ids)

    if (deleteError) {
      throw new Error(`Failed to delete batch: ${deleteError.message}`)
    }

    return ids.length
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private mapToExemplar(row: any): Exemplar {
    return {
      id: row.id,
      question: row.question,
      answer: row.answer,
      category: row.category,
      tags: row.tags || [],
      sourceType: row.source_type,
      sourceConversationId: row.source_conversation_id || undefined,
      sourceMessageId: row.source_message_id || undefined,
      status: row.status,
      errorMessage: row.error_message || undefined,
      isActive: row.is_active,
      createdBy: row.created_by,
      lastEditedBy: row.last_edited_by || undefined,
      createdAt: row.created_at,
      updatedAt: row.updated_at,
    }
  }
}
